import axios from 'axios';

/**
 * Zentraler SmartVolt API-Client — Phase 2 finalisiert (Cookie-only Auth).
 *
 * SICHERHEIT:
 *   - `withCredentials: true`  → sendet & empfängt httpOnly `sv_session`-Cookie
 *   - Auto-CSRF-Header für POST/PUT/PATCH/DELETE (Double-Submit-Pattern)
 *   - Bei 401 → CSRF-Cache leeren + Redirect auf /login
 *
 * KEINE LOCALSTORAGE-TOKENS MEHR:
 *   - Die JWT-Session liegt ausschließlich im httpOnly-Cookie (XSS-safe).
 *   - Kein `Authorization: Bearer …` mehr aus localStorage.
 *   - Beim App-Start räumt `AuthContext` alte localStorage-Reste einmalig weg.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CSRF_COOKIE_NAME = 'sv_csrf';
const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

// -- Kleine Cookie-Utility (kein document.cookie Parsing überall duplizieren) --
export const getCookie = (name) => {
  if (typeof document === 'undefined') return '';
  const match = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(name + '='));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : '';
};

// -- Axios-Instance ----------------------------------------------------------
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true, // sendet sv_session-Cookie automatisch mit
  headers: { 'Content-Type': 'application/json' } });

// Wir cachen den CSRF-Token in-memory, um bei jedem Write-Call NICHT
// wieder document.cookie zu parsen.
let cachedCsrfToken = '';

/** Holt (und cached) einen frischen CSRF-Token vom Backend. */
export const ensureCsrfToken = async () => {
  if (cachedCsrfToken) return cachedCsrfToken;
  const cookieVal = getCookie(CSRF_COOKIE_NAME);
  if (cookieVal) {
    cachedCsrfToken = cookieVal;
    return cachedCsrfToken;
  }
  try {
    const res = await axios.get(`${API_URL}/auth/csrf-token`, { withCredentials: true });
    cachedCsrfToken = res.data?.csrf_token || getCookie(CSRF_COOKIE_NAME) || '';
    return cachedCsrfToken;
  } catch {
    // Backend nicht erreichbar? Wir lassen den Request trotzdem laufen —
    // der Server wird ihn ggf. mit 403 ablehnen, das wird korrekt propagiert.
    return '';
  }
};

/** Löscht den lokalen CSRF-Cache (z.B. bei Logout). */
export const clearCsrfCache = () => {
  cachedCsrfToken = '';
};

// -- Request-Interceptor: nur noch CSRF -------------------------------------
api.interceptors.request.use(async (config) => {
  const method = (config.method || 'get').toUpperCase();
  if (UNSAFE_METHODS.has(method)) {
    const csrf = await ensureCsrfToken();
    if (csrf) config.headers['X-CSRF-Token'] = csrf;
  }
  return config;
}, (err) => Promise.reject(err));

// -- Response-Interceptor: 401 → Session weg → Login -------------------------
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      clearCsrfCache();
      // Nur redirect, wenn wir nicht bereits auf /login sind
      // und nicht auf einer öffentlichen Seite (z.B. /kurse) —
      // dort ist 401 einfach „nicht eingeloggt", nicht „Session verloren".
      if (typeof window !== 'undefined') {
        const pathname = window.location.pathname || '';
        const isPublic = pathname === '/' ||
          pathname.startsWith('/login') ||
          pathname.startsWith('/register') ||
          pathname.startsWith('/kurse') ||
          pathname.startsWith('/kurs/') ||
          pathname.startsWith('/probelektion') ||
          pathname.startsWith('/setup-password') ||
          pathname.startsWith('/danke') ||
          pathname.startsWith('/pro') ||
          pathname.startsWith('/impressum') ||
          pathname.startsWith('/datenschutz') ||
          pathname.startsWith('/agb');
        if (!isPublic) {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// ---------------------------------------------------------------------------
// FETCH-WRAPPER (für Code, der bereits mit `fetch()` arbeitet)
// ---------------------------------------------------------------------------
// Konvention: statt globalem `fetch(url)` einfach `apiFetch(pfad)` benutzen.
// `pfad` darf mit "/api/..." oder ohne "/api" beginnen; wir kanonisieren.
export const apiFetch = async (pathOrUrl, init = {}) => {
  const method = (init.method || 'GET').toUpperCase();
  // Robust: erlaubt String ODER URL-Objekt als Eingabe.
  const raw = typeof pathOrUrl === 'string' ? pathOrUrl : String(pathOrUrl);
  const url = raw.startsWith('http')
    ? raw
    : `${process.env.REACT_APP_BACKEND_URL}${raw.startsWith('/') ? raw : '/' + raw}`;

  const headers = new Headers(init.headers || {});
  if (init.body && !headers.has('Content-Type') && typeof init.body === 'string') {
    headers.set('Content-Type', 'application/json');
  }
  if (UNSAFE_METHODS.has(method)) {
    const csrf = await ensureCsrfToken();
    if (csrf && !headers.has('X-CSRF-Token')) headers.set('X-CSRF-Token', csrf);
  }

  let res = await fetch(url, {
    ...init,
    method,
    credentials: 'include',
    headers });

  // CSRF-Retry: bei 403 mit CSRF-Fehler einmal Token invalidieren + neu holen
  // und den Request erneut senden. Deckt Race-Condition ab, wenn ein
  // vorheriger 401 den Cache leert bevor der aktuelle Write-Call losläuft.
  if (res.status === 403 && UNSAFE_METHODS.has(method)) {
    try {
      const clone = res.clone();
      const errBody = await clone.text();
      if (errBody && errBody.toLowerCase().includes('csrf')) {
        clearCsrfCache();
        const freshCsrf = await ensureCsrfToken();
        if (freshCsrf) {
          headers.set('X-CSRF-Token', freshCsrf);
          res = await fetch(url, {
            ...init,
            method,
            credentials: 'include',
            headers });
        }
      }
    } catch {
      // ignore — original 403 wird nach unten durchgereicht
    }
  }

  // Bei 401 auf geschützten Routen → Login-Redirect (analog zu Axios)
  if (res.status === 401 && typeof window !== 'undefined') {
    const pathname = window.location.pathname || '';
    const isPublic = pathname === '/' ||
      pathname.startsWith('/login') ||
      pathname.startsWith('/register') ||
      pathname.startsWith('/kurse') ||
      pathname.startsWith('/kurs/') ||
      pathname.startsWith('/probelektion') ||
      pathname.startsWith('/setup-password') ||
      pathname.startsWith('/danke') ||
      pathname.startsWith('/pro') ||
      pathname.startsWith('/impressum') ||
      pathname.startsWith('/datenschutz') ||
      pathname.startsWith('/agb');
    if (!isPublic) {
      clearCsrfCache();
      window.location.href = '/login';
    }
  }
  return res;
};

// -- Auth-API ----------------------------------------------------------------
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  logout: async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      clearCsrfCache();
    }
  } };

// -- Employee-API ------------------------------------------------------------
export const employeeAPI = {
  getAll: () => api.get('/employees'),
  create: (data) => api.post('/employees', data),
  delete: (id) => api.delete(`/employees/${id}`) };

// -- Kurs-API ----------------------------------------------------------------
export const courseAPI = {
  getAll: () => api.get('/courses'),
  getById: (id) => api.get(`/courses/${id}`) };

// -- Enrollment-API ----------------------------------------------------------
export const enrollmentAPI = {
  create: (data) => api.post('/enrollments', data),
  getAll: () => api.get('/enrollments'),
  complete: (id, quizResult) => api.put(`/enrollments/${id}/complete`, quizResult),
  confirmPayment: (id, paymentId) => api.put(`/enrollments/${id}/payment/confirm`, { paymentId }) };

// -- Certificate-API ---------------------------------------------------------
export const certificateAPI = {
  getAll: () => api.get('/certificates'),
  download: (id) => api.get(`/certificates/${id}/download`, { responseType: 'blob' }) };

// -- Pricing-API -------------------------------------------------------------
export const pricingAPI = {
  calculate: (courseId, employeeCount) =>
    api.get('/pricing/calculate', { params: { courseId, employeeCount } }) };

export default api;
